import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../../store/userStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { useVehiclesSubscription } from "../../hooks/useVehiclesSubscription";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import useIsReseller from "../../hooks/useIsReseller";

import { Header } from "../../components/base/Header"; 
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { ENVIOS_MENU, ENVIOS_BASE } from "./config";
import {
  useDashboardCounts,
  type DashboardCounts,
} from "../../features/dashboard/useDashboardCounts";

const MENU_COUNT_KEYS: Record<string, keyof DashboardCounts | undefined> = {
  todos: "shipments",
  "links-seguimiento": "trackingLinks",
  "envios-inteligentes": "smartShipments",
  rutas: "routes",
  viajes: "trips",
};
import { getSettings, type EnviosSettings } from "./services/enviosService";

export default function EnviosLayout() {
  const { t } = useTranslation("envios");
  const { t: tCommon } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  useVehiclesSubscription();

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [settings, setSettings] = useState<EnviosSettings>({});

  // Companies store
  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);

  const { isReseller } = useIsReseller();

  const { data: counts, isLoading: countsLoading } = useDashboardCounts(companyId);

  useEffect(() => {
    getSettings()
      .then((s) => setSettings(s))
      .catch(() => {});
  }, []);

  // Sincronizar companyId desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar todas las compañías para el selector
  useEffect(() => {
    fetchAllCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = ENVIOS_MENU.map((item) => {
    const countKey = MENU_COUNT_KEYS[item.id];
    return {
      id: item.id,
      label: t(`menu.${item.id}`),
      icon: item.icon,
      path: `${ENVIOS_BASE}/${item.id}`,
      loading: !!countKey && countsLoading,
      count: countKey ? counts?.[countKey] : undefined,
    };
  });

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`h-screen pt-safe-top flex flex-col bg-app`}>
      {isExpiringSoon && (
        <div className="bg-warning/10 text-warning border-b border-warning/30 px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{tCommon("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={settings.appearance?.headerTitle || t("layout.headerTitle")}
        subtitle={settings.appearance?.headerSubtitle || t("layout.headerSubtitle")}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        onToggleMobileMenu={openMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isReseller={isReseller}
        showCompanySelector={true}
        companies={allCompanies}
        selectedCompany={companyId ?? null}
        onCompanyChange={(id) => setCompanyId(id ?? null)}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={handleNavigate}
          isActiveRoute={(itemId) => location.pathname.startsWith(`${ENVIOS_BASE}/${itemId}`)}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id)}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          companyIndicator={{
            companyId,
            companyName:
              allCompanies.find((c) => c.id === companyId)?.name ||
              allCompanies.find((c) => c.id === companyId)?.business_name ||
              companyId ||
              "test",
            isReseller,
          }}
          footerStats={{
            label: t("layout.headerTitle"),
            items: [{ label: "Envíos", value: 42, className: "text-primary-500" }],
          }}
        />

        <div className="flex-1 max-h-full overflow-auto min-w-0 w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="envios"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
